import type { BruteForcePlayer } from './types';

/**
 * Generate all combinations of k elements from an array
 * Uses iterative approach to avoid stack overflow on large inputs
 */
function* combinations<T>(array: T[], k: number): Generator<T[]> {
  if (k === 0) {
    yield [];
    return;
  }
  if (k > array.length || k < 0) {
    return;
  }

  if (k === array.length) {
    yield array.slice();
    return;
  }

  if (k === 1) {
    for (const item of array) {
      yield [item];
    }
    return;
  }

  // Use indices to track current combination
  const indices = Array.from({ length: k }, (_, i) => i);
  const n = array.length;

  while (true) {
    // Yield current combination
    yield indices.map((i) => array[i]);

    // Find rightmost index that can be incremented
    let i = k - 1;
    while (i >= 0 && indices[i] === n - k + i) {
      i--;
    }

    // If no index can be incremented, we're done
    if (i < 0) {
      break;
    }

    // Increment the found index and reset all following indices
    indices[i]++;
    for (let j = i + 1; j < k; j++) {
      indices[j] = indices[j - 1] + 1;
    }
  }
}

/**
 * Divide players into thirds based on rating
 *
 * @param players - Players to divide
 * @param useFormAdjusted - Use form-adjusted rating instead of overall rating
 */
function divideIntoThirds(
  players: BruteForcePlayer[],
  useFormAdjusted: boolean = false
): {
  topThird: BruteForcePlayer[];
  middleThird: BruteForcePlayer[];
  bottomThird: BruteForcePlayer[];
} {
  // Sort by rating (highest first)
  const sorted = [...players].sort((a, b) =>
    useFormAdjusted
      ? b.formAdjustedRating - a.formAdjustedRating
      : b.overallRating - a.overallRating
  );

  const totalPlayers = sorted.length;
  const thirdSize = Math.ceil(totalPlayers / 3);

  return {
    topThird: sorted.slice(0, thirdSize),
    middleThird: sorted.slice(thirdSize, thirdSize * 2),
    bottomThird: sorted.slice(thirdSize * 2),
  };
}

/**
 * How many players Blue takes from each tier (top, middle, bottom), for every
 * allowed way of hitting the requested team sizes. Orange gets the rest.
 *
 * - Each tier splits in half; a tier with an odd count sends its spare player
 *   to either team (so 15 → 8 v 7 and 14 → 7 v 7 work — both used to find no
 *   valid split and throw).
 * - Uneven teams also keep the original placements: the larger team takes one
 *   extra from any single tier, as before, so existing searches are unchanged.
 */
function planTierSplits(
  tierSizes: [number, number, number],
  blueSize: number,
  orangeSize: number
): Array<[number, number, number]> {
  const plans = new Map<string, [number, number, number]>();
  const add = (plan: [number, number, number]) => {
    const valid =
      plan.every((k, i) => k >= 0 && k <= tierSizes[i]) &&
      plan[0] + plan[1] + plan[2] === blueSize;
    if (valid) plans.set(plan.join('-'), plan);
  };

  // Halves, with each odd tier's spare player on either side
  const choices = tierSizes.map((t) => (t % 2 === 0 ? [t / 2] : [Math.floor(t / 2), Math.ceil(t / 2)]));
  for (const top of choices[0]) {
    for (const middle of choices[1]) {
      for (const bottom of choices[2]) add([top, middle, bottom]);
    }
  }

  // Original uneven-team placements: the larger team takes +1 in one tier
  if (blueSize !== orangeSize) {
    const base = tierSizes.map((t) => Math.floor(t / 2));
    for (let tier = 0; tier < 3; tier++) {
      const larger = base.map((b, i) => b + (i === tier ? 1 : 0)) as [number, number, number];
      add(blueSize > orangeSize ? larger : (tierSizes.map((t, i) => t - larger[i]) as [number, number, number]));
    }
  }

  return [...plans.values()];
}

/**
 * Options for combination generation. When `blueOutfieldSize + orangeOutfieldSize`
 * is odd, the surplus player rotates through each tier — the brute-force loop
 * picks whichever placement gives the best score.
 */
export interface CombinationOptions {
  /** Outfield slots to fill on Blue (after any permanent-GK pre-assignment). Defaults to floor(total/2). */
  blueOutfieldSize?: number;
  /** Outfield slots to fill on Orange. Defaults to total - blueOutfieldSize. */
  orangeOutfieldSize?: number;
}

/**
 * Generate all valid team combinations with the spread constraint
 *
 * The spread constraint ensures each team has an equal number of players
 * from each third (top, middle, bottom) of the rating distribution.
 *
 * For 18 players (6 per third, 9 per team):
 * - Each team gets 3 from top, 3 from middle, 3 from bottom
 * - Combinations: C(6,3) * C(6,3) * C(6,3) = 20 * 20 * 20 = 8,000
 *
 * For 12 players (4 per third, 6 per team):
 * - Each team gets 2 from top, 2 from middle, 2 from bottom
 * - Combinations: C(4,2) * C(4,2) * C(4,2) = 6 * 6 * 6 = 216
 *
 * Odd tiers and uneven teams (17 → 9 v 8, 15 → 8 v 7, or 8 v 9 outfield with a
 * permanent GK): see planTierSplits — every allowed placement of the spare
 * players is yielded and the brute-force scorer picks the best.
 *
 * @param players - All players to distribute
 * @param useFormAdjusted - Use form-adjusted rating for tier sorting
 * @param options - Optional asymmetric team sizes (for permanent GK support)
 * @yields Tuples of [blueOutfield, orangeOutfield]
 */
export function* generateValidCombinations(
  players: BruteForcePlayer[],
  useFormAdjusted: boolean = false,
  options: CombinationOptions = {}
): Generator<[BruteForcePlayer[], BruteForcePlayer[]]> {
  const totalPlayers = players.length;
  const defaultTeamSize = Math.floor(totalPlayers / 2);

  // When sizes aren't explicitly provided (the common no-permanent-GK path),
  // preserve the original behaviour of symmetric teams + dropping any surplus
  // player. When sizes ARE provided (permanent-GK path), respect them exactly.
  const sizesProvided = options.blueOutfieldSize !== undefined || options.orangeOutfieldSize !== undefined;
  const blueSize = options.blueOutfieldSize ?? defaultTeamSize;
  const orangeSize = options.orangeOutfieldSize ?? (sizesProvided ? totalPlayers - blueSize : defaultTeamSize);
  const effectiveTotal = blueSize + orangeSize;

  if (effectiveTotal > totalPlayers) {
    throw new Error(
      `[generateValidCombinations] Requested team sizes ${blueSize}+${orangeSize}=${effectiveTotal} exceeds player count ${totalPlayers}`
    );
  }

  const effectivePlayers = [...players]
    .sort((a, b) =>
      useFormAdjusted
        ? b.formAdjustedRating - a.formAdjustedRating
        : b.overallRating - a.overallRating
    )
    .slice(0, effectiveTotal);

  // Divide into thirds (using form-adjusted if enabled)
  const { topThird, middleThird, bottomThird } = divideIntoThirds(
    effectivePlayers,
    useFormAdjusted
  );

  const tierSizes: [number, number, number] = [topThird.length, middleThird.length, bottomThird.length];

  for (const [perThirdTop, perThirdMiddle, perThirdBottom] of planTierSplits(tierSizes, blueSize, orangeSize)) {
    for (const topCombo of combinations(topThird, perThirdTop)) {
      for (const midCombo of combinations(middleThird, perThirdMiddle)) {
        for (const bottomCombo of combinations(bottomThird, perThirdBottom)) {
          const blueTeam = [...topCombo, ...midCombo, ...bottomCombo];
          const blueIds = new Set(blueTeam.map((p) => p.player_id));
          const orangeTeam = effectivePlayers.filter((p) => !blueIds.has(p.player_id));

          yield [blueTeam, orangeTeam];
        }
      }
    }
  }
}

/**
 * Calculate the total number of valid combinations
 * Useful for progress reporting and performance estimation
 *
 * When `blueOutfieldSize` and `orangeOutfieldSize` are provided and differ,
 * counts the union of the three asymmetric placement variants.
 */
export function calculateTotalCombinations(
  playerCount: number,
  options: CombinationOptions = {}
): number {
  const defaultTeamSize = Math.floor(playerCount / 2);
  const sizesProvided = options.blueOutfieldSize !== undefined || options.orangeOutfieldSize !== undefined;
  const blueSize = options.blueOutfieldSize ?? defaultTeamSize;
  const orangeSize = options.orangeOutfieldSize ?? (sizesProvided ? playerCount - blueSize : defaultTeamSize);
  const effectiveTotal = blueSize + orangeSize;
  const thirdSize = Math.ceil(effectiveTotal / 3);

  // C(n, k) = n! / (k! * (n-k)!)
  function factorial(n: number): number {
    if (n <= 1) return 1;
    let result = 1;
    for (let i = 2; i <= n; i++) {
      result *= i;
    }
    return result;
  }

  function choose(n: number, k: number): number {
    if (k > n || k < 0) return 0;
    if (k === 0 || k === n) return 1;
    return factorial(n) / (factorial(k) * factorial(n - k));
  }

  // Reconstruct tier sizes (matching divideIntoThirds: top/middle = ceil(n/3), bottom = remainder)
  const tierSizes: [number, number, number] = [thirdSize, thirdSize, effectiveTotal - thirdSize * 2];

  let total = 0;
  for (const [perTop, perMid, perBot] of planTierSplits(tierSizes, blueSize, orangeSize)) {
    total += choose(tierSizes[0], perTop) * choose(tierSizes[1], perMid) * choose(tierSizes[2], perBot);
  }

  return total;
}

/**
 * Get tier distribution for a team assignment
 */
export function getTierDistribution(
  blueTeam: BruteForcePlayer[],
  orangeTeam: BruteForcePlayer[],
  allPlayers: BruteForcePlayer[],
  useFormAdjusted: boolean = false
): {
  blue: { top: number; middle: number; bottom: number };
  orange: { top: number; middle: number; bottom: number };
} {
  const { topThird, middleThird, bottomThird } = divideIntoThirds(
    allPlayers,
    useFormAdjusted
  );

  const topIds = new Set(topThird.map((p) => p.player_id));
  const midIds = new Set(middleThird.map((p) => p.player_id));
  const bottomIds = new Set(bottomThird.map((p) => p.player_id));

  return {
    blue: {
      top: blueTeam.filter((p) => topIds.has(p.player_id)).length,
      middle: blueTeam.filter((p) => midIds.has(p.player_id)).length,
      bottom: blueTeam.filter((p) => bottomIds.has(p.player_id)).length,
    },
    orange: {
      top: orangeTeam.filter((p) => topIds.has(p.player_id)).length,
      middle: orangeTeam.filter((p) => midIds.has(p.player_id)).length,
      bottom: orangeTeam.filter((p) => bottomIds.has(p.player_id)).length,
    },
  };
}

/**
 * Calculate how many players changed tiers due to form adjustment
 */
export function calculateTierChangesFromForm(
  players: BruteForcePlayer[]
): number {
  // Get tiers without form adjustment
  const { topThird: topOrig, middleThird: midOrig, bottomThird: botOrig } =
    divideIntoThirds(players, false);
  const topOrigIds = new Set(topOrig.map((p) => p.player_id));
  const midOrigIds = new Set(midOrig.map((p) => p.player_id));
  const botOrigIds = new Set(botOrig.map((p) => p.player_id));

  // Get tiers with form adjustment
  const { topThird: topForm, middleThird: midForm, bottomThird: botForm } =
    divideIntoThirds(players, true);
  const topFormIds = new Set(topForm.map((p) => p.player_id));
  const midFormIds = new Set(midForm.map((p) => p.player_id));
  const botFormIds = new Set(botForm.map((p) => p.player_id));

  // Count players who changed tiers
  let changes = 0;
  for (const player of players) {
    const wasTop = topOrigIds.has(player.player_id);
    const wasMid = midOrigIds.has(player.player_id);
    const wasBot = botOrigIds.has(player.player_id);

    const isTop = topFormIds.has(player.player_id);
    const isMid = midFormIds.has(player.player_id);
    const isBot = botFormIds.has(player.player_id);

    if (wasTop !== isTop || wasMid !== isMid || wasBot !== isBot) {
      changes++;
    }
  }

  return changes;
}
